import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Layout, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import axiosInstance from '../../services/axiosInstance';

const { Title, Text } = Typography;
const { Header, Content } = Layout;

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const validateFields = () => {
    const newErrors = { username: '', password: '' };
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = 'Please enter your username';
      isValid = false;
    }

    if (!password.trim()) {
      newErrors.password = 'Please enter your password';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleLogin = async (e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
    }

    // Clear previous login error
    setLoginError('');

    if (!validateFields()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/api/login/', {
        username: username.trim(),
        password: password,
      });

      const { token } = response.data;

      if (!token) {
        throw new Error('No token received from server');
      }

      // Store token in localStorage
      localStorage.setItem('authToken', token);

      message.success({
        content: 'Login successful! Redirecting...',
        duration: 2,
      });

      // Call the success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess({ token });
      }
    } catch (error) {
      console.error('Login error:', error);

      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (error.response) {
        // Server responded with an error
        const status = error.response.status;
        const data = error.response.data;

        if (status === 401) {
          errorMessage = 'Invalid username or password. Please try again.';
        } else if (status === 400) {
          errorMessage = data?.message || data?.error || data?.detail || 'Invalid request. Please check your input.';
        } else if (status === 403) {
          errorMessage = 'Access denied. Your account may be inactive.';
        } else if (status === 404) {
          errorMessage = 'Login service not found. Please contact support.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = data?.message || data?.error || data?.detail || `Error: ${status}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error in both alert and toast
      setLoginError(errorMessage);
      message.error({
        content: errorMessage,
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'username') {
      setUsername(value);
      if (errors.username) {
        setErrors(prev => ({ ...prev, username: '' }));
      }
    } else {
      setPassword(value);
      if (errors.password) {
        setErrors(prev => ({ ...prev, password: '' }));
      }
    }
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img
          src="https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg"
          alt="Indian Flag"
          style={{ height: '32px', marginRight: '10px' }}
        />
        <Title level={3} style={{ color: 'white', margin: 0 }}>
          GP Admin Panel
        </Title>
      </Header>
      <Content
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #001529 0%, #042035 100%)',
          padding: '24px',
        }}
      >
        <Card
          style={{
            width: '100%',
            maxWidth: 400,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            borderRadius: '12px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <img
              src="https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg"
              alt="Indian Flag"
              style={{ height: '48px', marginBottom: '16px' }}
            />
            <Title level={2} style={{ margin: 0, color: '#001529' }}>
              Admin Login
            </Title>
            <Text type="secondary">
              Sign in to access the admin panel
            </Text>
          </div>

          {/* Show error alert if login failed */}
          {loginError && (
            <Alert
              message={loginError}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
              closable
              onClose={() => setLoginError('')}
            />
          )}

          <Form layout="vertical" onSubmitCapture={handleLogin}>
            <Form.Item
              label="Username"
              validateStatus={errors.username ? 'error' : ''}
              help={errors.username}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#001529' }} />}
                placeholder="Enter your username"
                size="large"
                value={username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                style={{
                  borderRadius: '6px',
                  background: '#ffffff',
                  borderColor: '#d9d9d9',
                  color: '#000000',
                }}
                autoComplete="username"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="Password"
              validateStatus={errors.password ? 'error' : ''}
              help={errors.password}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#001529' }} />}
                placeholder="Enter your password"
                size="large"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                style={{
                  borderRadius: '6px',
                  background: '#ffffff',
                  borderColor: '#d9d9d9',
                  color: '#000000',
                }}
                autoComplete="current-password"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
              <Button
                type="primary"
                size="large"
                block
                loading={loading}
                htmlType="submit"
                style={{
                  height: '48px',
                  borderRadius: '6px',
                  background: '#001529',
                  borderColor: '#001529',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Content>
    </Layout>
  );
};

export default Login;
